import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { FileUpload, Loader, SdirButton } from '@sdir/sds';
import { AttachmentResult } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useCurrentApplication, useSubmitSequence } from '../../../services/hooks';
import { buildRequirementMetadata } from '../../../services/certreq/requirementKeys';
import {
  buildCertReqAttachmentSequence,
  certReqAllowedFileTypes,
  CertReqAttachmentParams,
  CertReqAttachmentTracker,
} from '../../../services/certreq/certreqAttachments';
import { useRequirementAttachments } from '../../../services/certreq/useRequirementAttachments';
import {
  nodeRequirementType,
  nodeText,
  UserNode,
} from '../../../services/certreq/userView/userRequirements';
import { UserViewTexts } from '../../../services/certreq/userView/texts';

interface RequirementUploadProps {
  node: UserNode;
  certificateCode: string;
  texts: UserViewTexts;
  /**
   * Bærer noden opplastingsknappen? Fillista står uansett — filene som er
   * levert hører til kravet, også når det ikke lenger er noe å legge til.
   */
  showButton: boolean;
}

/**
 * Opplasting av dokumentasjon for ett krav — eller for én hel gruppe.
 *
 * Filene knyttes til noden via `simulatorResultItemUri`
 * (`certreq:{kode}#{requirementId}`), mens `group` fortsatt er den
 * backend-genererte Guid-en. Kravtypen følger med i metadata, og avgjør både
 * AttachmentType-en og hvilke filtyper velgeren tilbyr — se
 * `certReqAttachmentType()`. Alt annet enn foto, navnetrekk og legitimasjon
 * lastes opp som AttachmentType.Na.
 *
 * En **gruppe** dokumenteres under ett, og bruker sin egen `requirementId` som
 * kravnøkkel — samme format som et enkeltkrav. Noder uten id kan ikke få en
 * stabil nøkkel: et enkeltkrav sier tydelig fra, mens en gruppe uten id bare
 * lar være å tilby opplasting (gruppen er en beholder, ikke et krav søkeren er
 * lovet å kunne dokumentere).
 *
 * Knappen står på **egen linje under radteksten**, ikke inne i linja: å laste
 * opp dokumentasjon er handlingen kravet ber om, og en handling som gjemmer seg
 * ytterst til høyre i en tett liste blir ikke funnet. Fillista følger rett under
 * knappen — det som er levert hører sammen med det som leverer. Fargen er
 * **radens**, ikke designsystemets blå: knappen hører til kravet den står under.
 */
export const RequirementUpload: React.FC<RequirementUploadProps> = ({
  node,
  certificateCode,
  texts,
  showButton,
}) => {
  const intl = useIntl();
  const { application, refresh } = useCurrentApplication();
  const [errors, setErrors] = useState<string[] | null>(null);

  const { requirementKey, attachments } = useRequirementAttachments(
    certificateCode,
    node.requirementId
  );

  const { submit, isSubmitting } = useSubmitSequence<
    CertReqAttachmentParams,
    CertReqAttachmentTracker
  >(buildCertReqAttachmentSequence);

  // Et enkeltkrav bærer typen selv; en gruppe har den bare når kravene under
  // den er enige om hva slags dokumentasjon gruppen ber om.
  const requirementType = nodeRequirementType(node);

  const runSequence = useCallback(
    async (files: File[], removedAttachments: AttachmentResult[]) => {
      if (!application.result || !requirementKey || !node.requirementId) return;
      setErrors(null);
      const { tracker } = await submit(
        {
          application: application.result,
          requirementKey,
          requirementType,
          metadata: buildRequirementMetadata({
            certificateCode,
            requirementId: node.requirementId,
            label: node.label,
            requirementType,
          }),
          existingGroup: attachments[0]?.group,
          files,
          removedAttachments,
        },
        {}
      );
      if (tracker.errors?.length) {
        setErrors(tracker.errors);
        return;
      }
      // Stille oppfriskning, av samme grunn som erklæringen: en opplasting midt
      // i kravtreet skal ikke avmontere siden og nullstille hvilke grupper
      // søkeren har åpnet.
      refresh?.(true);
    },
    [
      application.result,
      attachments,
      certificateCode,
      node.label,
      node.requirementId,
      refresh,
      requirementKey,
      requirementType,
      submit,
    ]
  );

  const handleNewFilesAdded = useCallback(
    (files: File[]) => {
      runSequence(files, []);
    },
    [runSequence]
  );

  const handleRemove = useCallback(
    (attachment: AttachmentResult) => {
      runSequence([], [attachment]);
    },
    [runSequence]
  );

  // Uten requirementId finnes ingen stabil nøkkel. Et enkeltkrav sier fra —
  // brukeren er lovet å kunne dokumentere det — mens en gruppe bare lar være.
  if (!requirementKey) {
    if (!showButton || node.kind !== 'requirement') return null;
    return <NoKeyInfo>{intl.formatMessage({ id: 'certreq.upload.missingkey' })}</NoKeyInfo>;
  }

  const text = nodeText(node);

  return (
    <Upload data-testid={`requirement-upload-${node.requirementId}`}>
      {showButton && (
        <UploadButtonRow>
          <FileUpload
            allowedTypes={certReqAllowedFileTypes(requirementType)}
            multiple
            buttonLabel=""
            onFilesSelected={handleNewFilesAdded}
            renderButton={({ onClick }) => (
              <SdirButton
                type="secondary"
                size="sm"
                htmlType="button"
                aria-label={texts.uploadFor(text)}
                text={attachments.length > 0 ? texts.addFile : texts.upload}
                onClick={onClick}
              />
            )}
          />
        </UploadButtonRow>
      )}

      {attachments.length > 0 && (
        <Files aria-label={texts.uploadedFilesFor(text)}>
          {attachments.map((attachment, index) => (
            <FileItem key={`${attachment.uid || index}-${attachment.blobName}`}>
              <FileName>{attachment.blobName}</FileName>
              <RemoveFile
                type="button"
                aria-label={texts.removeFile(attachment.blobName || '')}
                onClick={() => handleRemove(attachment)}
              >
                <span aria-hidden="true">✕</span>
              </RemoveFile>
            </FileItem>
          ))}
        </Files>
      )}

      {isSubmitting && <Loader />}

      {errors?.length ? (
        <ErrorMessage
          title={intl.formatMessage({ id: 'certreq.upload.error.title' })}
          error={errors.join(' ')}
        />
      ) : null}
    </Upload>
  );
};

const Upload = styled.div``;

/*
 * `FileUpload` er beholdt for filvalget: den eier den skjulte filvelgeren,
 * typesjekken og størrelsesgrensen. Bare drakten er vår — `renderButton` bytter
 * ut designsystemets egen knapp med en sekundærknapp i radens statusfarge.
 *
 * Containeren i `FileUpload` er dimensjonert for en opplastingsflate på en hel
 * side (`width: 100%; height: 6rem`), og må krympes til knappen sin her; ellers
 * ville hver eneste kravrad fått seks rems tomrom under seg. Id-velgeren er
 * komponentens egen, og vinner over dens klassestil.
 */
const UploadButtonRow = styled.div`
  margin-top: 0.5rem;

  #file_upload {
    width: auto;
    height: auto;
  }

  button {
    color: var(--status-text);
    border-color: var(--status-strong);
  }
`;

/**
 * Filene som ligger på kravet, rett under knappen som la dem der. Dempet — de
 * bekrefter hva som er levert, og skal ikke ta oppmerksomheten fra selve
 * kravet. Punktmarkørene er fjernet; navnene står som en enkel oppramsing.
 */
const Files = styled.ul`
  margin: 0.4rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.7rem;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.font.text75};
`;

const FileItem = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const FileName = styled.span`
  overflow-wrap: anywhere;
`;

/**
 * Fjern-knappen: like diskré som fillista selv, og først tydelig når musa eller
 * tastaturfokuset er på den. Treffflaten er større enn krysset, slik at den kan
 * treffes uten å sikte. Krysset er dekorasjon; `aria-label` er det
 * skjermleseren leser, og det navngir **fila** — står brukeren med fire sider
 * av et vitnemål, er det hvilken av dem hun fjerner som er spørsmålet.
 */
const RemoveFile = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 auto;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  color: inherit;

  &:hover {
    background: rgb(0 0 0 / 6%);
    color: ${({ theme }) => theme.colors.font.text};
  }
`;

const NoKeyInfo = styled.p`
  margin-top: 1rem;
  font-size: 14px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.font.text75};
`;

export default RequirementUpload;
